import * as THREE from 'three';

// Aether Flow Shader Animation
class AetherFlow {
  constructor(container) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.clock = new THREE.Clock();
    
    // Shader properties
    this.props = {
      hue: 260,
      speed: 0.15,
      intensity: 0.7,
      complexity: 6.0,
      warp: 0.4
    };

    this.init();
  }

  init() {
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.container.appendChild(this.renderer.domElement);

    const uniforms = {
      u_time: { value: 0.0 },
      u_resolution: { value: new THREE.Vector2(this.container.clientWidth, this.container.clientHeight) },
      u_mouse: { value: new THREE.Vector2(0.5, 0.5) },
      u_hue: { value: this.props.hue },
      u_speed: { value: this.props.speed },
      u_intensity: { value: this.props.intensity },
      u_complexity: { value: this.props.complexity },
      u_warp: { value: this.props.warp },
    };

    const vertexShader = `
      void main() {
        gl_Position = vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_mouse;
      uniform float u_hue;
      uniform float u_speed;
      uniform float u_intensity;
      uniform float u_complexity;
      uniform float u_warp;

      vec3 hsv2rgb(vec3 c) {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
      }
      
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
      }

      float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(random(i), random(i + vec2(1.0, 0.0)), u.x),
                   mix(random(i + vec2(0.0, 1.0)), random(i + vec2(1.0, 1.0)), u.x), u.y);
      }

      float fbm(vec2 st) {
        float value = 0.0;
        float amplitude = 0.5;
        for (int i = 0; i < 10; i++) {
          if (i >= int(u_complexity)) break;
          value += amplitude * noise(st);
          st *= 2.0;
          amplitude *= 0.5;
        }
        return value;
      }

      mat2 rotate(float angle) {
        return mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
      }

      void main() {
        vec2 uv = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);
        float t = u_time * u_speed;
        
        vec2 mouse_uv = (u_mouse * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);
        float warp_effect = smoothstep(0.7, 0.0, distance(uv, mouse_uv)) * u_warp;

        vec2 p = uv * rotate(t * 0.1) + warp_effect;
        
        float n1 = fbm(p * 1.2 + vec2(t * 0.1, t * 0.2));
        float n2 = fbm(p * 2.0 + n1 + vec2(-t * 0.25, t * 0.15));
        float n3 = fbm(p * 3.5 + n2 + vec2(t * 0.1, -t * 0.2));

        float final_noise = n1 * 0.6 + n2 * 0.25 + n3 * 0.15;

        float hue_shift = final_noise * 0.1;
        float saturation = 0.6 + final_noise * 0.4;
        float value = 0.15 + pow(final_noise, 2.5) * u_intensity;
        
        value += pow(smoothstep(0.7, 1.0, final_noise), 3.0) * 0.7 * u_intensity;

        vec3 color = hsv2rgb(vec3((u_hue / 360.0) + hue_shift, saturation, value));
        
        color *= 1.0 - smoothstep(0.8, 1.5, length(uv));

        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const geometry = new THREE.PlaneGeometry(2, 2);
    this.material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader });
    const mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(mesh);

    this.uniforms = uniforms;

    this.animate();
    this.setupEventListeners();
  }

  animate() {
    this.uniforms.u_time.value = this.clock.getElapsedTime();
    this.renderer.render(this.scene, this.camera);
    this.animationFrameId = requestAnimationFrame(() => this.animate());
  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.handleResize());
    window.addEventListener('mousemove', (e) => this.handleMouseMove(e));
  }

  handleResize() {
    const { clientWidth, clientHeight } = this.container;
    this.renderer.setSize(clientWidth, clientHeight);
    this.uniforms.u_resolution.value.set(clientWidth, clientHeight);
  }

  handleMouseMove(e) {
    this.uniforms.u_mouse.value.x = e.clientX;
    this.uniforms.u_mouse.value.y = window.innerHeight - e.clientY;
  }

  updateProps(props) {
    Object.assign(this.props, props);
    this.uniforms.u_hue.value = this.props.hue;
    this.uniforms.u_speed.value = this.props.speed;
    this.uniforms.u_intensity.value = this.props.intensity;
    this.uniforms.u_complexity.value = this.props.complexity;
    this.uniforms.u_warp.value = this.props.warp;
  }

  destroy() {
    cancelAnimationFrame(this.animationFrameId);
    window.removeEventListener('resize', () => this.handleResize());
    window.removeEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.container.removeChild(this.renderer.domElement);
    this.material.dispose();
    this.renderer.dispose();
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAetherFlow);
} else {
  initAetherFlow();
}

function initAetherFlow() {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.zIndex = '-10';
  container.style.pointerEvents = 'none';
  document.body.prepend(container);

  new AetherFlow(container);
}

export default AetherFlow;
